// 账户 / 积分 API(/api/auth/* 与 /api/credits*)。
// 会话靠 httponly cookie(rg_session),前端不接触 token;http 封装默认 same-origin 带 cookie。
import { http } from "./http";
import type { ApiResult, AuthOptions, AuthUser, CreditEvent, CreditsInfo, WechatBindingStatus } from "@/types";

/**
 * 兼容两种返回形状:后端可能直接返回 user 对象,也可能包一层 {user: {...}}。
 * 契约写法是 "→ {user}",两边实现容易理解不一致——这里统一拆包,永不因形状差异崩溃。
 */
function unwrapUser(res: ApiResult<unknown>): ApiResult<AuthUser> {
  if (!res.ok) return res;
  const raw = res.data as Record<string, unknown> | null;
  const user = raw && typeof raw === "object" && "user" in raw ? (raw as { user: unknown }).user : raw;
  if (!user || typeof user !== "object" || typeof (user as { username?: unknown }).username !== "string") {
    return { ok: false, error: "服务返回的用户信息格式异常,请稍后重试", retryable: true };
  }
  const u = user as AuthUser;
  // credits 兜底:即便后端漏了该字段,徽章也显示 0 而不是崩溃。
  if (!u.credits || typeof u.credits.total !== "number") {
    u.credits = { total: 0, daily: 0, bundle: 0, permanent: 0, free_expiring: 0 };
  }
  return { ok: true, data: u };
}

export const authApi = {
  /** 登出:删服务端 session + 清 cookie,204 无返回体。 */
  logout: () => http.post<null>("/api/auth/logout"),

  /** 当前登录用户;未登录 → 401 code=auth_required(守卫据此跳 /auth)。 */
  me: async () => unwrapUser(await http.get<unknown>("/api/auth/me")),

  /** 积分余额 + 价目表;任意鉴权请求都会触发当日免费积分惰性发放。 */
  credits: () => http.get<CreditsInfo>("/api/credits"),

  /** 兑换码(RG-XXXX-XXXX-XXXX):返回到账积分;套餐码额外返回 bundle 详情。 */
  redeem: (code: string) =>
    http.post<{
      granted: number;
      bundle?: { id: string; name: string; points: number; expires_at: number | null };
      balance: CreditsInfo["balance"];
    }>("/api/credits/redeem", { code }),

  /** 积分流水(最近 limit 条)。 */
  history: (limit = 50) => http.get<{ events: CreditEvent[] }>(`/api/credits/history?limit=${limit}`),

  /** admin 造自定义积分兑换码;非 admin → 403。 */
  adminCreateCodes: (amount: number, count: number) =>
    http.post<{ codes: string[] }>("/api/admin/codes", { amount, count }),

  /** admin 造积分套餐兑换码。 */
  adminCreateBundleCodes: (bundleId: string, count: number) =>
    http.post<{ codes: string[] }>("/api/admin/codes", { bundle_id: bundleId, count }),

  /** 登录能力开关(公开):微信登录是否启用、每日发放量。 */
  options: () => http.get<AuthOptions>("/api/auth/options", { timeoutMs: 10_000 }),

  wechatBinding: (signal?: AbortSignal) =>
    http.get<WechatBindingStatus>("/api/auth/wechat/binding", { signal, timeoutMs: 10_000 }),

  startWechatBinding: () =>
    http.post<{ authorization_url: string }>("/api/auth/wechat/bind/start", { confirm: true }, { timeoutMs: 15_000 }),

  /** 注销账号(不可恢复):输入完整用户名确认。204 无返回体。 */
  deleteAccount: (confirm_username: string) =>
    http.post<null>("/api/auth/delete-account", { confirm_username }),
};
